import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Volume2, ChevronDown, ChevronUp } from "lucide-react";
import { makeProgressKey, loadProgress, saveProgress, clearProgress } from "../data/progressStore";

const OPTION_LABELS = ["1", "2", "3", "4"];

// ── Instruction line based on material type ──────────────────
function getInstruction(material) {
  if (!material) return "音声を聴いてください。";
  switch (material.type) {
    case "chart":   return "次の統計グラフを見ながら音声を聴いてください。";
    case "table":   return "次の表を見ながら音声を聴いてください。";
    case "ad":      return "次の広告を見ながら音声を聴いてください。";
    case "notice":  return "次のお知らせを見ながら音声を聴いてください。";
    case "poster":  return "次のポスターを見ながら音声を聴いてください。";
    case "program": return "次の資料を見ながら音声を聴いてください。";
    case "quote":   return "次の見積書を見ながら音声を聴いてください。";
    case "flow":    return "次のフロー図を見ながら音声を聴いてください。";
    case "format":  return "次の書式を見ながら音声を聴いてください。";
    default:        return "次の資料を見ながら音声を聴いてください。";
  }
}

// ── Bar chart renderer for type='chart' ──────────────────────
const BAR_COLORS = ["#4a7adb", "#c0392b", "#c9a227", "#2ecc71", "#9b59b6"];

function ChartMaterial({ material }) {
  const rows = material.content;
  // Parse percentage values
  const parsed = rows.map(([label, val]) => ({
    label,
    val,
    num: parseFloat(val) || 0,
  }));
  const maxVal = Math.max(...parsed.map((r) => r.num), 1);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs text-gray-500 mb-3">
        <span className="mr-1">📊</span>
        資料：{material.title}
      </p>
      <div className="flex items-end gap-6 justify-center" style={{ height: 110 }}>
        {parsed.map((row, i) => (
          <div key={i} className="flex flex-col items-center gap-1" style={{ minWidth: 56 }}>
            <span className="text-sm font-bold" style={{ color: BAR_COLORS[i % BAR_COLORS.length] }}>
              {row.val}
            </span>
            <div
              className="w-10 rounded-t"
              style={{
                height: Math.max(8, (row.num / maxVal) * 80),
                backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
              }}
            />
            <span className="text-xs text-gray-600 text-center leading-tight">{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Table renderer ────────────────────────────────────────────
function TableMaterial({ material }) {
  const rows = material.content;
  const isHeader = (row) =>
    row.some((cell) => typeof cell === "string" && /項目|年|会社|フェーズ|順位|①|年度/.test(cell));
  const headerRow = rows.length > 0 && isHeader(rows[0]) ? rows[0] : null;
  const bodyRows = headerRow ? rows.slice(1) : rows;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs text-gray-500 mb-2">
        <span className="mr-1">📋</span>資料：{material.title}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          {headerRow && (
            <thead>
              <tr>
                {headerRow.map((cell, i) => (
                  <th key={i} className="border border-gray-300 bg-gray-100 px-2 py-1 text-left font-semibold text-gray-700">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {bodyRows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border border-gray-200 px-2 py-1 text-gray-700">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Text-based material renderer (ad / notice / poster / etc.) ─
function TextMaterial({ material }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs text-gray-500 mb-2">
        <span className="mr-1">📄</span>資料：{material.title}
      </p>
      <pre className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed font-sans">
        {Array.isArray(material.content)
          ? material.content.map((row) => (Array.isArray(row) ? row.join("　") : row)).join("\n")
          : material.content}
      </pre>
    </div>
  );
}

// ── Material dispatcher ───────────────────────────────────────
function MaterialRenderer({ material }) {
  if (!material) return null;
  if (material.type === "chart") return <ChartMaterial material={material} />;
  if (material.type === "table" || material.type === "program" || material.type === "quote" || material.type === "format")
    return <TableMaterial material={material} />;
  return <TextMaterial material={material} />;
}

// ── Audio script panel ────────────────────────────────────────
function AudioScriptPanel({ script }) {
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  function handlePlay() {
    if (!window.speechSynthesis || !script) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(script);
    utter.lang = "ja-JP";
    utter.rate = 0.9;
    const voices = window.speechSynthesis.getVoices();
    const jaVoice = voices.find((v) => v.lang.startsWith("ja"));
    if (jaVoice) utter.voice = jaVoice;
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utter);
  }

  function handleStop() {
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }

  return (
    <div className="rounded-lg overflow-hidden mb-3">
      {/* Dark header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800">
        <div className="flex items-center gap-2">
          <Volume2 size={14} className="text-blue-400" />
          <span className="text-sm text-white font-medium">音声スクリプト</span>
        </div>
        <div className="flex items-center gap-2">
          {speaking ? (
            <button
              onClick={handleStop}
              className="text-xs px-2 py-1 rounded bg-red-700 text-white hover:bg-red-600"
            >
              停止
            </button>
          ) : (
            <button
              onClick={handlePlay}
              className="text-xs px-2 py-1 rounded bg-blue-700 text-white hover:bg-blue-600"
            >
              再生
            </button>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1 text-xs text-gray-300 border border-gray-600 rounded px-2 py-1 hover:bg-gray-700"
          >
            {open ? (
              <>
                スクリプトを非表示 <ChevronUp size={12} />
              </>
            ) : (
              <>
                スクリプトを表示 <ChevronDown size={12} />
              </>
            )}
          </button>
        </div>
      </div>
      {/* Script body */}
      {open && (
        <div className="bg-gray-900 px-4 py-3">
          <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-line">{script}</p>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function ListeningReadingModuleView2({ questions }) {
  const progressKey = makeProgressKey(questions);

  // 初始化时同步从 localStorage 读取，避免 useEffect 竞态问题
  const [currentIndex, setCurrentIndex] = useState(
    () => loadProgress(progressKey).currentIndex ?? 0
  );
  const [answers, setAnswers] = useState(
    () => loadProgress(progressKey).answers ?? {}
  );

  const question = questions[currentIndex] || null;
  const userAnswer = question ? answers[question.id] : undefined;
  const answered = userAnswer !== undefined;
  const isCorrect = answered && userAnswer === question?.answer;

  const correct = questions.filter((q) => answers[q.id] === q.answer).length;
  const attempted = questions.filter((q) => answers[q.id] !== undefined).length;
  const rate = attempted > 0 ? ((correct / attempted) * 100).toFixed(0) + "%" : "—%";

  function handleSelect(idx) {
    if (!question || answered) return;
    const newAnswers = { ...answers, [question.id]: idx };
    setAnswers(newAnswers);
    saveProgress(progressKey, newAnswers, currentIndex);
  }

  function handlePrev() {
    const prev = Math.max(0, currentIndex - 1);
    setCurrentIndex(prev);
    saveProgress(progressKey, answers, prev);
  }

  function handleNext() {
    const next = Math.min(questions.length - 1, currentIndex + 1);
    setCurrentIndex(next);
    saveProgress(progressKey, answers, next);
  }

  function handleReset() {
    clearProgress(progressKey);
    setAnswers({});
    setCurrentIndex(0);
  }

  if (!question) {
    return (
      <div className="flex items-center justify-center h-32">
        <p className="text-gray-400 text-sm">暂无题目</p>
      </div>
    );
  }

  return (
    <div className="flex gap-4 flex-1 min-w-0">
      {/* ── Main question area ─────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {/* Question header */}
        <div className="border border-gray-200 rounded-xl overflow-hidden mb-3">
          {/* Nav */}
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-100">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-1 text-sm text-gray-500 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
            >
              <ChevronLeft size={14} /> 上一题
            </button>
            <span className="text-xs text-gray-400">
              {currentIndex + 1} / {questions.length}
            </span>
            <button
              onClick={handleNext}
              disabled={currentIndex >= questions.length - 1}
              className="flex items-center gap-1 text-sm text-gray-500 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
            >
              下一题 <ChevronRight size={14} />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-6 py-4">
            {/* Q number + instruction */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-base font-bold text-gray-800">Q{question.id}</span>
                <span className="text-sm text-gray-700">{getInstruction(question.material)}</span>
              </div>
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1 shrink-0" />
            </div>

            {/* Material */}
            {question.material && (
              <div className="mb-4">
                <MaterialRenderer material={question.material} />
              </div>
            )}

            {/* Audio script */}
            {question.audioScript && (
              <AudioScriptPanel script={question.audioScript} />
            )}

            {/* Question text */}
            <p className="text-sm font-semibold text-gray-800 mb-4">{question.question}</p>

            {/* Options — 2×2 grid */}
            <div className="grid grid-cols-2 gap-2">
              {question.options.map((opt, i) => {
                const isSelected = userAnswer === i;
                const isCorrectOpt = question.answer === i;

                let style = "border-gray-200 bg-white text-gray-700 hover:bg-gray-50";
                if (answered) {
                  if (isCorrectOpt)       style = "border-green-400 bg-green-50 text-green-700";
                  else if (isSelected)    style = "border-red-400 bg-red-50 text-red-600";
                } else if (isSelected) {
                  style = "border-blue-400 bg-blue-50 text-blue-700";
                }

                return (
                  <button
                    key={i}
                    onClick={() => handleSelect(i)}
                    disabled={answered}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors disabled:cursor-default text-left ${style}`}
                  >
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        answered && isCorrectOpt
                          ? "bg-green-500 text-white"
                          : answered && isSelected
                          ? "bg-red-400 text-white"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {OPTION_LABELS[i]}
                    </span>
                    <span className="leading-snug">{opt}</span>
                  </button>
                );
              })}
            </div>

            {/* Explanation */}
            {answered && question.explanation && (
              <div className="mt-4 px-3 py-2 rounded-lg bg-blue-50 border-l-4 border-blue-400">
                <span className="text-xs font-semibold text-blue-700">
                  正解：{OPTION_LABELS[question.answer]}
                </span>
                <span className="text-xs text-gray-700">{question.explanation}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Right: answer sheet ──────────────────────────────── */}
      <div className="w-52 shrink-0">
        <div className="border border-gray-200 rounded-xl p-3 bg-white sticky top-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-600">答题卡</span>
            <button
              onClick={handleReset}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              重置
            </button>
          </div>

          {/* Stats */}
          <div className="flex gap-2 mb-3 text-center">
            <div className="flex-1 bg-green-50 rounded-lg py-1.5">
              <p className="text-lg font-bold text-green-600">{correct}</p>
              <p className="text-[10px] text-gray-400">正确</p>
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg py-1.5">
              <p className="text-lg font-bold text-gray-500">{attempted}</p>
              <p className="text-[10px] text-gray-400">已答</p>
            </div>
            <div className="flex-1 bg-indigo-50 rounded-lg py-1.5">
              <p className="text-sm font-bold text-indigo-500 leading-6">{rate}</p>
              <p className="text-[10px] text-gray-400">正确率</p>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-4 gap-1">
            {questions.map((q, idx) => {
              const ua = answers[q.id];
              const isAnswered = ua !== undefined;
              const isCur = idx === currentIndex;
              const isOk = isAnswered && ua === q.answer;

              let bg = "bg-gray-100 text-gray-400";
              if (isCur)          bg = "bg-orange-400 text-white";
              else if (isAnswered) bg = isOk ? "bg-green-400 text-white" : "bg-red-400 text-white";

              return (
                <button
                  key={q.id}
                  onClick={() => { setCurrentIndex(idx); saveProgress(progressKey, answers, idx); }}
                  className={`h-8 rounded text-xs font-medium transition-colors ${bg}`}
                >
                  {q.id}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-col gap-1 text-[10px] text-gray-400">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-orange-400 inline-block" /> 当前题
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-green-400 inline-block" /> 回答正确
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-red-400 inline-block" /> 回答错误
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
